/**
 * Plain-English words under every row of the edition Matrix (Jay, 2026-11-01:
 * "I need descriptions against each feature in the matrix so I understand
 * exactly what I am turning off").
 *
 * `does` = what the feature gives a workspace, in the words a customer would use.
 * `off`  = what people on that plan lose when the cell is unticked. Records a
 *          workspace already has are never deleted by a switch; the sentence
 *          says what stops working or disappears.
 *
 * The Matrix also lists, under each row, every other row that switches off
 * with it (worked out from the catalogue's dependencies, so it is never out of
 * date). Keep each sentence short. A test checks every catalogue row has one.
 */
export interface MatrixDescription {
  does: string;
  off: string;
}

export const MATRIX_DESCRIPTIONS: Record<string, MatrixDescription> = {
  // ── Modules (the grey header rows) ────────────────────────────────────────
  'modules.assets': {
    does: 'The asset register: every asset and place, its details, history, photos and documents.',
    off: 'The Assets tab goes, and almost everything else switches off with it. Never untick this for a real plan.',
  },
  'modules.sites': {
    does: 'Sites group assets by location, with a site page, site health and site reports.',
    off: 'Sites disappear from the menu and asset pages; creating a site is refused. Assets keep working without a site.',
  },
  'modules.requests': {
    does: 'People (staff, tenants, clients) report an issue or ask for work, and you triage it. Also covers sharing an asset with outsiders.',
    off: 'Report an issue, the Requests list and sharing an asset are locked; new requests and shares are refused.',
  },
  'modules.inspections': {
    does: 'Run checklists on assets: start, fill in, complete and report inspections.',
    off: 'Inspections cannot be started or completed, and every inspection-based journey switches off with it.',
  },
  'modules.jobs': {
    does: 'Raise, assign and complete jobs (repairs and maintenance work).',
    off: 'Jobs cannot be created or worked, and everything built on jobs switches off with it.',
  },
  'modules.reviews': {
    does: 'Sign-off of completed work before it is closed.',
    off: 'Always included: follows Approvals, so it is on whenever Approvals is on.',
  },
  'modules.reports': {
    does: 'Asset and site reports, including the AI asset and site reports, and report exports.',
    off: 'The Report buttons on assets and sites lock with an upgrade prompt; AI asset and site reports are refused.',
  },
  'modules.analytics': {
    does: 'The Insights tab: KPIs, trends and charts across the workspace.',
    off: 'Insights moves under "Upgrade to use" in the menu and its page shows the upgrade prompt.',
  },
  'modules.contacts': {
    does: 'Contacts: the outside people and organisations you work with.',
    off: 'Contacts moves under "Upgrade to use" and its page shows the upgrade prompt; contractors switch off with it.',
  },
  'modules.contractors': {
    does: 'The contractor directory and the contractor home for outside firms.',
    off: 'Contractor pages show the upgrade prompt; contractor management and quotes journeys switch off.',
  },
  'modules.teams': {
    does: 'More than one person in a workspace: teams, members, roles and invitations.',
    off: 'Inviting people and team pages lock; new teams and invites are refused. The owner can still work alone.',
  },
  'modules.automation': {
    does: 'Heading for scheduled and recurring work (Scheduling, Recurring work, Maintenance programmes).',
    off: 'Switches off automation features listed below it, including automation recipes in Integrations.',
  },
  'modules.integrations': {
    does: 'Connect AssetWRX to other systems: imports, Power BI, Power Automate, IoT and embeds.',
    off: 'New integration connections lock with an upgrade prompt; the integration features below switch off.',
  },
  'modules.ai': {
    does: 'Heading for the AI features. Each AI feature underneath has its own switch.',
    off: 'Always included: switch the individual AI rows instead.',
  },
  'modules.administration': {
    does: 'Managing the workspace: people, account, trust centre and feature settings.',
    off: 'Always included: every workspace has to manage its account.',
  },
  'modules.settings': {
    does: 'Personal and workspace settings and preferences.',
    off: 'Always included: every workspace has settings.',
  },
  'modules.developer': {
    does: 'Developer tools: API keys, the API explorer and service accounts.',
    off: 'The Advanced / developer pages show the upgrade prompt; REST, GraphQL, webhooks and MCP switch off.',
  },

  // ── Assets ────────────────────────────────────────────────────────────────
  'capabilities.qr_codes': {
    does: 'Print QR labels for assets and scan them to open the asset.',
    off: 'QR label printing and QR options on asset pages and the asset list are hidden.',
  },
  'capabilities.barcode_scan': {
    does: 'Scan a barcode to fill in an asset identifier or an inspection answer.',
    off: 'The barcode scan buttons on asset create and inspection answers are hidden; typing still works.',
  },
  'capabilities.condition_intelligence': {
    does: 'Rule-based condition and risk advice on assets, and the best-fit technician hint when scheduling.',
    off: 'Condition advice on recurring work and the technician-fit hints in the scheduler are hidden.',
  },
  'capabilities.location_tracking': {
    does: 'Records where an asset has been, from inspections, jobs and connected sources.',
    off: 'Location history stops recording (new location claims are refused).',
  },
  'capabilities.knowledge_base': {
    does: 'How-tos, FAQs, notes and files on assets, the Knowledge search page and login-free share links.',
    off: 'Knowledge moves under "Upgrade to use", its page shows the upgrade prompt and adding knowledge is refused.',
  },
  'capabilities.custom_asset_types': {
    does: 'Your own asset types with their own fields, beyond Place, Equipment, Vehicle and Other.',
    off: 'Asset types opens on the upgrade prompt and new types are refused. Types already made stay usable and editable.',
  },
  'capabilities.bulk_asset_setup': {
    does: 'Create many places or assets at once, or a whole building from a facility preset.',
    off: 'Bulk create and facility presets are hidden and refused; assets are added one at a time.',
  },

  // ── Requests ──────────────────────────────────────────────────────────────
  'capabilities.approvals': {
    does: 'Approve or decline requests and completed work before they move on.',
    off: 'Requests, reviews, schedule governance and customer approval all switch off with it.',
  },

  // ── Inspections ───────────────────────────────────────────────────────────
  'capabilities.photo_capture': {
    does: 'Take and attach photos as evidence on inspections, jobs and requests.',
    off: 'Always included: inspections and jobs need photos.',
  },
  'capabilities.video_capture': {
    does: 'Attach short videos as evidence.',
    off: 'The video option is hidden on inspections; photos still work.',
  },
  'capabilities.drawing': {
    does: 'Draw on photos to mark up defects with arrows and circles.',
    off: 'The markup tool on photos is hidden.',
  },
  'capabilities.voice_dictation': {
    does: 'Speak instead of typing in notes, answers and the assistant.',
    off: 'Microphone buttons for dictation are hidden; typing still works.',
  },
  'capabilities.speech_recognition': {
    does: 'The phone\'s speech-to-text that powers voice dictation.',
    off: 'Always included: switch Voice dictation instead.',
  },
  'capabilities.ml_kit': {
    does: 'The phone reads text and codes in photos (meters, serial plates, barcodes).',
    off: 'Always included: part of photo capture.',
  },
  'capabilities.evidence_intelligence': {
    does: 'Checks photos and documents for dates, meter readings and compliance evidence, using AI only when asked.',
    off: 'The evidence check button is hidden and its AI call is refused; the compliance journey switches off.',
  },
  'capabilities.wrx_vision': {
    does: 'Scan an inspection item with the camera: reads meters, identifiers and barcodes and suggests answers to review.',
    off: 'Vision scan options are hidden on checklists and inspections, and all cloud Vision features switch off.',
  },
  'capabilities.wrx_vision_cloud': {
    does: 'Cloud analysis of a Vision scan: condition, defects and not-observed checks against the checklist.',
    off: 'Cloud Vision analysis is refused; the on-phone Vision reading still works. Vision packs switch off.',
  },
  'capabilities.wrx_vision_pack_fleet': {
    does: 'Vision checks for vehicles: dents, scratches, glass, lights, tyres and wheels.',
    off: 'Vision stops offering the vehicle checks.',
  },
  'capabilities.wrx_vision_pack_equipment': {
    does: 'Vision checks for plant and equipment: guards, leaks, hoses, cables, corrosion, plates and meters.',
    off: 'Vision stops offering the equipment checks.',
  },
  'capabilities.wrx_vision_pack_property': {
    does: 'Vision checks for rooms and buildings: damp, mould-like growth, cracks, glazing, fittings and access.',
    off: 'Vision stops offering the property checks.',
  },
  'capabilities.wrx_vision_pack_safety': {
    does: 'Vision checks for safety equipment: extinguishers, alarms, signage and access (never certifies).',
    off: 'Vision stops offering the safety equipment checks.',
  },
  'capabilities.wrx_vision_training_share': {
    does: 'Lets reviewed Vision photos be used to improve Vision (the workspace owner can also turn it off).',
    off: 'No photos from this plan are used to improve Vision, and the sharing card is hidden.',
  },
  'capabilities.group_rules': {
    does: 'One rule schedules the same checklist on every matching asset, and keeps adding it to matching assets created later.',
    off: 'Group rules moves under "Upgrade to use", its page opens on the upgrade prompt and new rules are refused. Existing rules keep running and can still be stopped.',
  },

  // ── Jobs ──────────────────────────────────────────────────────────────────
  'capabilities.wrx_verified': {
    does: 'Completion evidence on jobs: expected outcomes, before/after photos and an evidence check the reviewer decides on.',
    off: 'The Verified options on job create and the evidence check on completion are hidden.',
  },
  'capabilities.work_intelligence': {
    does: 'AI help on a piece of work: reads the evidence and suggests what to do next.',
    off: 'The work intelligence panel is hidden and its AI call is refused.',
  },
  'capabilities.work_planning': {
    does: 'Plan who does what and when: people-by-time calendar, capacity, travel, parts and skills, and suggested plans.',
    off: 'The planning calendar, plan slots on jobs and planning insights are hidden or locked with an upgrade prompt.',
  },
  'capabilities.schedule_exceptions': {
    does: 'Field staff ask to skip, move or reassign scheduled work, and a manager decides.',
    off: 'The skip / reschedule / reassign requests on scheduled work are hidden.',
  },
  'capabilities.rounds': {
    does: 'One job that walks a checklist across several assets in order.',
    off: 'Start a round is hidden and refused; existing rounds can still be finished.',
  },
  'capabilities.contractor_inspections': {
    does: 'Give an inspection to a contractor with an AssetWRX account; it shows in their work and they run the checklist.',
    off: 'Contractors are not offered when giving an inspection, and the server refuses it; inspections already with a contractor can be finished.',
  },
  'capabilities.sub_jobs': {
    does: 'A job holds sub-jobs, each with its own person or contractor, date and checks. The approver signs off the whole job once. Search: sub-jobs, family.',
    off: 'Creating sub-jobs is hidden and refused everywhere (app, assistant, API). Jobs that already have sub-jobs keep working to the end: check, sign off, cancel, move dates.',
  },

  // ── Reports ───────────────────────────────────────────────────────────────
  'capabilities.compliance_register': {
    does: 'A workspace-wide register of certificates, renewals and overdue evidence.',
    off: 'Compliance moves under "Upgrade to use" and its page shows the upgrade prompt.',
  },

  // ── Insights ──────────────────────────────────────────────────────────────
  'capabilities.journey_intelligence': {
    does: 'AI commentary on operational journeys (planned).',
    off: 'Always included: not built yet, so there is nothing to switch off.',
  },
  'capabilities.journey_analytics': {
    does: 'Journey-flow dashboards and funnels on the Insights page.',
    off: 'The journey charts on Insights are hidden.',
  },
  'capabilities.asset_360': {
    does: 'Asset 360: one report per asset for the chosen reader, with comparisons, value and life, versions, sharing and the AI version.',
    off: 'The Asset 360 button opens the upgrade prompt; the API, Power BI and assistant stop reading Asset 360.',
  },
  'capabilities.esg': {
    does: 'Energy, carbon and water from meters, F-gas leak checks, EPC, the ESG tab, Portfolio ESG and the 360 chapter. Per plan: Off, Add-on (per asset) or Included.',
    off: 'ESG tabs and Portfolio ESG show the add-on prompt; 360 reports drop the ESG chapter; API, Power BI, MCP and assistant stop reading ESG. Meter readings keep working.',
  },
  'capabilities.esg_ai_writeup': {
    does: 'The AI write-up on the ESG tab: a summary of the figures and recommended actions with buttons. Uses AI credits.',
    off: 'The ESG tab shows the figures without the AI write-up.',
  },
  'capabilities.site_360': {
    does: 'Site 360: one report per site across its assets, with comparisons, value, versions, sharing and the AI version.',
    off: 'The Site 360 button opens the upgrade prompt; the API, Power BI and assistant stop reading Site 360.',
  },
  'capabilities.power_bi_export': {
    does: 'Read workspace data into Power BI or Excel through the OData feed.',
    off: 'The Power BI connection and its API section lock; the feed refuses this plan.',
  },

  // ── Contacts / Contractors ────────────────────────────────────────────────
  'capabilities.contact_import': {
    does: 'Import contacts from the phone\'s address book or a file.',
    off: 'Import contacts is hidden and its pages show the upgrade prompt; contacts are added one at a time.',
  },
  'capabilities.quotes': {
    does: 'Ask contractors for quotes on a job, compare them and pick one.',
    off: 'Request quotes locks with an upgrade prompt and new quote requests are refused.',
  },

  // ── Automation ────────────────────────────────────────────────────────────
  'capabilities.scheduling': {
    does: 'Scheduled work: due dates on the calendar and the base for anything that repeats.',
    off: 'Switches off everything that repeats or is planned (listed below), including Group rules and Recurring work.',
  },
  'capabilities.recurring_work': {
    does: 'Inspections and jobs that repeat on a schedule (every month, every year).',
    off: 'Recurring work locks with an upgrade prompt and new series are refused. Maintenance programmes and industry packs switch off.',
  },
  'capabilities.maintenance_programmes': {
    does: 'Multi-stage maintenance plans driven by meter readings, dates or a sequence of stages.',
    off: 'Maintenance plans and meter readings are hidden or locked; new ones are refused.',
  },

  // ── Integrations ──────────────────────────────────────────────────────────
  'capabilities.csv_import': {
    does: 'Import assets and contacts from a CSV file.',
    off: 'The CSV import pages show the upgrade prompt.',
  },
  'capabilities.excel_import': {
    does: 'Import structured data from an Excel workbook.',
    off: 'The Excel import option in Integrations is locked.',
  },
  'capabilities.power_automate': {
    does: 'Trigger Microsoft Power Automate flows when things happen in AssetWRX.',
    off: 'The Power Automate connection is refused for this plan.',
  },
  'capabilities.iot_connectors': {
    does: 'Bring in sensor (IoT) signals that can raise work automatically.',
    off: 'IoT connections and the IoT section on asset pages are hidden; the API refuses IoT signals.',
  },
  'capabilities.embed': {
    does: 'Show AssetWRX views (work, assets, inspections, jobs, reports) inside another website or portal.',
    off: 'Embedded views stop loading for this plan; interactive embeds switch off too.',
  },
  'capabilities.embed_interactive': {
    does: 'Lets embedded views change things (update assets, do inspections, change jobs), not only show them.',
    off: 'Embedded views become read-only.',
  },

  // ── AI ────────────────────────────────────────────────────────────────────
  'capabilities.ai_summary': {
    does: 'AI-written summaries and drafts, such as the inspection summary.',
    off: 'Summarise / draft with AI buttons are hidden and the AI call is refused.',
  },
  'capabilities.ai_assistants': {
    does: 'The AssetWRX assistant: ask questions and get help in a chat.',
    off: 'The assistant button and menu entry go under "Upgrade to use"; every assistant feature below switches off.',
  },
  'capabilities.field_ai_assist': {
    does: 'AI help on a text field: improve, summarise or dictate right where you type.',
    off: 'The AI assist button on text fields shows a lock with an upgrade prompt.',
  },
  'capabilities.ai_checklist_generation': {
    does: 'Draft a checklist with AI from a description, a photo or a document.',
    off: '"Generate with AI" is hidden when creating a checklist; manual checklists still work.',
  },
  'capabilities.assistant_changes': {
    does: 'The assistant prepares new or updated assets, sites, inspections, jobs and group rules for a person to confirm.',
    off: 'The assistant can answer questions but cannot prepare changes.',
  },
  'capabilities.assistant_planning': {
    does: 'The assistant plans days, balances workload, suggests times and advises on maintenance programmes.',
    off: 'The assistant stops offering planning and scheduling help.',
  },
  'capabilities.assistant_insights': {
    does: 'The assistant explains trends, costs and causes, prepares reports and sends a weekly briefing.',
    off: 'The assistant stops giving insights, reports and the weekly briefing.',
  },
  'capabilities.assistant_photo': {
    does: 'Ask the assistant about a photo you take or pick.',
    off: 'The photo button in the assistant is hidden.',
  },
  'capabilities.assistant_voice': {
    does: 'Talk to the assistant and hear its replies (needs the latest store app).',
    off: 'The speaker and voice options in the assistant are hidden.',
  },

  // ── Administration / Settings ─────────────────────────────────────────────
  'capabilities.feature_requests': {
    does: 'Customers request features, follow their requests and vote on ideas.',
    off: 'Request a feature and Ideas pages show the upgrade prompt and new requests are refused.',
  },
  'capabilities.industry_packs': {
    does: 'Install ready-made checklist and maintenance-plan packs for an industry, with suggestions per asset.',
    off: 'Industry pack install and suggestions are hidden or locked.',
  },
  'capabilities.offline_mode': {
    does: 'Keep working without signal; changes sync when the phone reconnects.',
    off: 'Always included: field work must keep working without signal.',
  },
  'capabilities.notifications': {
    does: 'In-app, push and email notifications about work.',
    off: 'Always included: people must hear about work given to them.',
  },

  // ── Developer ─────────────────────────────────────────────────────────────
  'capabilities.rest_api': {
    does: 'The REST API: other systems read and write workspace data with an API key.',
    off: 'API keys, service accounts and the API explorer show the upgrade prompt; the API refuses this plan.',
  },
  'capabilities.graphql': {
    does: 'The GraphQL API for read-heavy queries.',
    off: 'GraphQL requests are refused for this plan.',
  },
  'capabilities.webhooks': {
    does: 'Signed webhooks that tell other systems when something changes.',
    off: 'Webhooks cannot be set up and are not sent for this plan.',
  },
  'capabilities.mcp': {
    does: 'MCP access, so outside AI agents can work with the workspace.',
    off: 'The AI settings / MCP page shows the upgrade prompt and MCP requests are refused.',
  },

  // ── Journeys (end-to-end flows built from the rows above) ─────────────────
  'journeys.request_to_inspection': {
    does: 'Turn a request into an inspection in one step.',
    off: 'The "Inspect it" option on a request locks with an upgrade prompt.',
  },
  'journeys.request_to_job': {
    does: 'Turn an approved request into a job in one step.',
    off: 'The "Make it a job" option on a request locks with an upgrade prompt.',
  },
  'journeys.inspection_to_job': {
    does: 'Raise follow-up jobs from failed inspection items.',
    off: 'Always included: on whenever Inspections and Jobs are on.',
  },
  'journeys.inspection_to_report': {
    does: 'Produce a report from a completed inspection.',
    off: 'Always included: on whenever Inspections and Reports are on.',
  },
  'journeys.job_completion': {
    does: 'Complete, review and close out a job.',
    off: 'Always included: on whenever Jobs and Reviews are on.',
  },
  'journeys.asset_lifecycle': {
    does: 'Commission, take out of service, make safe and recommission an asset, with the history kept.',
    off: 'Always included: on whenever its modules are on.',
  },
  'journeys.asset_onboarding': {
    does: 'Capture and register a new asset.',
    off: 'Always included: on whenever Assets is on.',
  },
  'journeys.asset_retirement': {
    does: 'Retire an asset for good, with a reason and a disposal record.',
    off: 'Always included: on whenever Assets is on.',
  },
  'journeys.preventive_maintenance': {
    does: 'Planned maintenance that repeats across assets.',
    off: 'Always included: on whenever Inspections and Recurring work are on.',
  },
  'journeys.reactive_maintenance': {
    does: 'Respond to breakdowns and one-off issues.',
    off: 'Always included: on whenever Jobs and Requests are on.',
  },
  'journeys.compliance': {
    does: 'Track certificates, documents and compliance status end to end.',
    off: 'Always included: on whenever Reports and Evidence intelligence are on.',
  },
  'journeys.contractor_management': {
    does: 'Engage contractors and manage their quotes end to end.',
    off: 'Always included: on whenever Contractors and Quotes are on.',
  },
  'journeys.customer_approval': {
    does: 'Send work to a customer to approve and schedule.',
    off: 'Customer approval steps are switched off for this plan.',
  },
  'journeys.iot_alert_to_inspection': {
    does: 'A sensor alert raises an inspection automatically.',
    off: 'Always included: on whenever Inspections and IoT connectors are on.',
  },

  // ── Platform services (engines behind the features) ───────────────────────
  'services.auth': {
    does: 'Sign-in and identity.',
    off: 'Everything depends on this. Unticking it switches the whole app off for the plan. Never untick.',
  },
  'services.permissions': {
    does: 'Who can see and change what (roles and membership).',
    off: 'Teams and administration depend on this. Never untick for a real plan.',
  },
  'services.audit': {
    does: 'The history of every change.',
    off: 'Always included: the audit trail is always kept.',
  },
  'services.storage': {
    does: 'Storing photos, documents and reports.',
    off: 'Photos, assets, reports and knowledge all depend on this. Never untick for a real plan.',
  },
  'services.search': {
    does: 'Search and lookup across assets, contacts and more.',
    off: 'Assets, contacts and QR codes depend on this. Never untick for a real plan.',
  },
  'services.offline_sync': {
    does: 'Syncing changes made offline.',
    off: 'Always included: changes made offline always sync.',
  },
  'services.notification_engine': {
    does: 'Sends in-app, push and email notifications.',
    off: 'Scheduling, approvals, quotes and more switch off with it (listed below).',
  },
  'services.reporting_engine': {
    does: 'Builds reports, branding and exports.',
    off: 'Reports, Insights, compliance and Power BI switch off with it (listed below).',
  },
  'services.ai_platform': {
    does: 'Runs every AI feature and charges AI credits.',
    off: 'Every AI feature switches off with it (listed below).',
  },
  'services.mlkit': {
    does: 'On-phone reading of barcodes, labels and text.',
    off: 'Barcode scan and evidence and work intelligence switch off with it (listed below).',
  },
  'services.work_intelligence': {
    does: 'The engine behind Work intelligence.',
    off: 'Work intelligence switches off with it.',
  },
  'services.evidence_intelligence': {
    does: 'The engine behind Evidence intelligence.',
    off: 'Evidence intelligence and the compliance journey switch off with it.',
  },
  'services.journey_analytics_engine': {
    does: 'The engine behind journey analytics.',
    off: 'Journey analytics switches off with it.',
  },
  'services.automation_engine': {
    does: 'Runs scheduled and recurring work in the background.',
    off: 'Automation and recurring work switch off with it (listed below).',
  },
  'services.integration_engine': {
    does: 'Runs imports and data connections.',
    off: 'Integrations, imports, Power Automate and IoT switch off with it (listed below).',
  },
  'services.api_platform': {
    does: 'The public API gateway.',
    off: 'REST, GraphQL, MCP and the developer pages switch off with it (listed below).',
  },
  'services.graphql_platform': {
    does: 'The GraphQL gateway.',
    off: 'GraphQL switches off with it.',
  },
  'services.webhook_platform': {
    does: 'Sends outbound webhooks.',
    off: 'Webhooks switch off with it.',
  },
  'services.mcp_platform': {
    does: 'The MCP bridge for outside AI agents.',
    off: 'MCP switches off with it.',
  },
};
